import { Modal, Grid, Text, useMantineTheme, Title, Button } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useRollbar } from '@rollbar/react';
import { IconDownload, IconPlayerPlayFilled } from '@tabler/icons-react';
import { Variants, motion } from 'framer-motion';
import Image from 'next/image';
import { useRef } from 'react';
import ReactPlayer from 'react-player';
import { Content } from '../data/content';
import { useRouter } from 'next/router';

export const VideoLibrary = ({ contents }: { contents: Content[] }) => {
  const scrollRef = useRef(null);
  const itemMotion: Variants = {
    hidden: { opacity: 0, y: 50 },
    show: {
      transition: {
        mass: 1,
        duration: 0.6,
        type: 'tween',
      },
      opacity: [0, 0.2, 0.3, 0.5, 1],
      y: 0,
      height: '100%',
    },
  };

  return (
    <Grid>
      {contents?.map((video, index) => (
        <Grid.Col key={index} sm={4} md={3} span={6}>
          <motion.div
            initial="hidden"
            whileInView="show"
            variants={itemMotion}
            viewport={{ root: scrollRef, once: true }}
          >
            <VideoCard {...video} />
          </motion.div>
        </Grid.Col>
      ))}
    </Grid>
  );
};

export const VideoCard = ({ title, fileUrl, thumbnailUrl }: Content) => {
  const rollbar = useRollbar();
  const [modalOpened, { close: modalClose, open: modalOpen }] = useDisclosure(false);

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const router = useRouter();
  const locale = router.locale || 'pt-BR';
  const isEn = locale === 'en-US';

  return (
    <div className="bg-black backdrop-blur-sm w-full h-full bg-opacity-40 rounded-lg overflow-hidden">
      <button
        title="Open image"
        onClick={modalOpen}
        className="overflow-hidden relative group w-full"
        style={{ aspectRatio: '5/7' }}
      >
        {thumbnailUrl && (
          <Image
            className="transition-all duration-150 group-hover:scale-110 group-hover:brightness-50"
            fill
            style={{ objectFit: 'cover', objectPosition: 'center 70%' }}
            src={thumbnailUrl}
            alt=""
          />
        )}
        <div className="transition-all duration-150 absolute opacity-0 group-hover:opacity-100 group-hover:scale-110 top-1/2 text-white left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <IconPlayerPlayFilled
            style={{
              margin: 'auto',
              border: '2px solid rgb(255,255,255, 0.5)',
              borderRadius: '50%',
              padding: '8px',
            }}
            size={80}
          />
        </div>
      </button>

      <a
        title="Download file"
        href={fileUrl}
        download
        className="flex min-h-[50px] -mt-[3px] justify-between items-center p-4 gap-4 hover:bg-white hover:bg-opacity-10"
      >
        <Text ta="left" weight="bold" size="md" lineClamp={3} style={{ zIndex: 1 }}>
          {title[locale] || (isEn ? 'Download' : 'Baixar')}
        </Text>

        <div className="w-max h-fit">
          <IconDownload size={24} style={{ marginBottom: 3 }} />
        </div>
      </a>

      <Modal
        opened={modalOpened}
        onClose={modalClose}
        size="auto"
        styles={{
          body: {
            ...(isMobile && { padding: 0 }),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          },
          header: { ...(isMobile && { marginTop: 60, marginBottom: 10 }) },
          content: { flex: '1 !important' },
        }}
        fullScreen={isMobile}
        id="modal-vid"
        centered
        style={{ position: 'absolute', height: '100%' }}
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        <ReactPlayer
          controls={true}
          playing={true}
          width={'auto'}
          height={'100%'}
          style={{
            aspectRatio: '10 / 16',
            margin: 'auto',
            maxHeight: isMobile ? '100%' : 'calc(100vh - 350px)',
          }}
          url={fileUrl}
          onError={(e) => {
            rollbar.error('Error playing press kit video', {
              error: e,
              url: fileUrl,
              message: e?.message,
            });
          }}
        />

        {title && (
          <Title mx="md" mt="md">
            {title[locale]}
          </Title>
        )}

        <Button
          m="md"
          variant="default"
          component="a"
          href={fileUrl}
          download
          leftIcon={<IconDownload size={18} style={{ marginBottom: 3 }} />}
        >
          Download
        </Button>
      </Modal>
    </div>
  );
};
